import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { coordinateRefresh, createApiClient, isApiError as isPackageApiError } from '@oprus/api-client';
import { useTenantStore } from '@/stores/tenantStore';
import { useCompanyStore } from '@/stores/companyStore';
import { getFriendlyError } from '@/lib/errors';
import i18n from '@/locales';
import type { ApiPaginatedResponse, PaginatedResponse } from '@/types/api';
import { withCookieSessionLock } from './session-lock';
import { shouldRefreshBeforeRequest } from './token-expiry';
import { reportErrorDeferred } from '@/lib/report-error-deferred';
import { isTutorialMode } from '@/tutorial/mode';
import {
  AUTH_REFRESH_TOKEN_KEY,
  AUTH_TOKEN_KEY,
  clearAuthPersistence,
} from './auth-storage';
import { installCompanyContextRecovery } from '@/lib/company-context-recovery';

export const SECURE_SESSION = import.meta.env.VITE_CRM_SECURE_SESSION === 'true';
export const API_URL = SECURE_SESSION
  ? new URL('/api', window.location.origin).href
  : import.meta.env.VITE_API_URL || 'http://localhost:4000';
export const WS_URL = import.meta.env.VITE_WS_URL || API_URL;

/** Legacy access-cookie experiment stays separate from the complete CRM session.
 * Secure mode keeps access JWTs in memory for API/socket handshakes. Only the
 * same-origin proxy receives the HttpOnly refresh cookie; no token is persisted.
 */
export const HTTPONLY_AUTH = import.meta.env.VITE_HTTPONLY_AUTH === 'true';

const PUBLIC_AUTH_ENDPOINTS = [
  '/auth/login',
  '/auth/mfa/login',
  '/auth/mfa/resend',
  '/auth/refresh',
  '/auth/session-status',
  '/auth/logout',
  '/auth/register',
  '/auth/register-with-plan',
  '/auth/redeem-invite-code',
  '/auth/verify-checkout-session',
  '/auth/forgot-password',
  '/auth/reset-password',
  // Login Google (FA): start/enabled/exchange sao publicos — 401 aqui (codigo de
  // troca vencido) e' erro do fluxo, nunca motivo para refresh/logout.
  '/auth/google/',
  // Endpoints publicos lidos PRE-login: um 401 aqui (ex.: tenant nao resolvido)
  // NUNCA deve disparar refresh/logout — senao causa loop infinito em /auth.
  '/settings/public',
];

/** Resolve relative /files/ paths to absolute API URLs */
export function getFileUrl(path: string): string {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  if (path.startsWith('/files/')) return `${API_URL}${path}`;
  if (path.startsWith('data:')) return path;
  return `${API_URL}/files/${path}`;
}

// ----------------------------------------------------------------------------
// Cliente HTTP — Onda 10 (ADR-0014): a instancia base agora vem de
// `createApiClient` (pacote compartilhado `@oprus/api-client`), que cuida de:
// criacao da instancia axios (baseURL/Content-Type/timeout/adapter) e injecao
// do `Authorization: Bearer <token>` via `getToken`. Os 2 headers dinamicos
// (X-Tenant-Id/X-Company-Id) tambem migraram, via `getExtraHeaders` abaixo —
// mesma logica que o antigo interceptor de request tinha (store > fallback
// VITE_TENANT_ID para chamadas publicas pre-login).
//
// O que NAO migrou para o pacote (fica autoral do CRM, em camada por cima da
// instancia retornada por `createApiClient` — ver o response interceptor mais
// abaixo) e por que:
//   1. Unwrap automatico do envelope `{ data }` no sucesso — o pacote nao tem
//      esse hook (seu handler de sucesso e a identidade); a Onda 6 deste app
//      depende dele para ~80 services que ja leem `response.data` desembrulhado.
//   2. PUBLIC_AUTH_ENDPOINTS (anti-loop: 401 em /auth/login, /auth/register
//      etc. nunca deve disparar refresh/logout). O callback `refreshToken` do
//      pacote (`CreateApiClientConfig.refreshToken: () => Promise<string|null>`)
//      NAO recebe a URL do request que falhou — so sabe "houve um 401" — entao
//      nao ha como pedir pro pacote pular o refresh so nesses endpoints sem
//      estender `CreateApiClientConfig` (mudaria os outros apps que consomem
//      o pacote; fora de escopo desta onda).
//   3. A fila/mutex de refresh (`isRefreshing`/`failedQueue`) preservando o
//      formato de erro exato que os ~80 services/hooks ja esperam
//      (`AxiosError`, via `isApiError`/`axios.isAxiosError`): o interceptor de
//      resposta do pacote SEMPRE converte a rejeicao para `ApiError` (nunca
//      preserva o `AxiosError` original) — isso quebraria o contrato publico
//      deste arquivo (`rejects.toBeInstanceOf(AxiosError)`, `isApiError`,
//      `getErrorMessage`, etc.). Por isso o response interceptor abaixo
//      primeiro "desfaz" esse wrapping — via `unwrapClientError`, usando
//      `ApiError.raw`, que o pacote preserva — antes de rodar a MESMA logica
//      de refresh+fila+PUBLIC_AUTH_ENDPOINTS que este arquivo ja tinha.
//
// `maxRetries: 0` desliga o retry automatico de erro de rede que o pacote
// oferece (backoff exponencial, ate 3 tentativas por default): o CRM nunca
// re-tentou sozinho em falha de rede, e esta onda nao muda comportamento
// observavel. `timeoutMs` agora e `DEFAULT_REQUEST_TIMEOUT_MS` (ver doc da
// constante abaixo) — ate a Onda 12 era `0`, herdado do `axios.create()`
// manual, ou seja, requisicao NENHUMA tinha limite.
// ----------------------------------------------------------------------------

/**
 * Timeout padrao aplicado a TODA requisicao HTTP do CRM.
 *
 * Ate a Onda 12 o cliente era criado com `timeoutMs: 0` — literalmente "sem
 * limite". Um upstream lento (LiteLLM, Evolution API, Twilio, um export
 * travado no backend) deixava a promise pendurada indefinidamente: o spinner
 * girava para sempre, sem erro, sem toast e sem log — o usuario so saia
 * recarregando a pagina. Isso viola `.claude/rules/robustness.md` ("IO externo
 * sempre com limite"): sem timeout nao existe caminho de erro, e sem caminho
 * de erro nao existe tratamento.
 *
 * 30s e o mesmo default do `@oprus/api-client` (usado pelos outros layouts),
 * entao esta escolha alinha o CRM com o resto da plataforma em vez de criar
 * mais um numero magico.
 */
const DEFAULT_REQUEST_TIMEOUT_MS = 30_000;

/**
 * Timeout estendido para as poucas chamadas que legitimamente ultrapassam os
 * 30s do default — nao por lentidao de rede, mas porque a requisicao carrega
 * bytes demais (em qualquer direcao) ou porque o servidor faz um trabalho caro
 * inline, sem enfileirar.
 *
 * Cada call site passa `{ timeout: LONG_REQUEST_TIMEOUT_MS }` na config da
 * propria chamada — o axios usa a config por-request quando presente e so cai
 * no default da instancia quando ela e omitida.
 *
 * DELIBERADAMENTE sem numero de linha: a lista anterior citava linhas, elas
 * saíram do lugar na primeira edicao seguinte e o comentario passou meses
 * mentindo ("os 5 call sites" quando ja eram 7). Referencia por FUNCAO, que
 * sobrevive a refactor.
 *
 * 1) Downloads — `responseType: 'blob'`; o servidor gera o arquivo sob demanda
 *    e o tempo cresce com o tamanho do dataset (7):
 *      - `leads.ts`             → `leadsService.exportCsv`
 *      - `customers.ts`         → `customersService.exportCsv`
 *      - `reports.ts`           → `exportReport`
 *      - `ai.ts`                → `exportConversation`
 *      - `knowledge.service.ts` → `downloadDocument`
 *      - `lgpd.ts`              → `exportSubject`
 *      - `audit-logs.ts`        → `exportCsv`
 *
 * 2) Uploads — `multipart/form-data` (6). O `timeout` do axios cobre a
 *    requisicao INTEIRA, incluindo o tempo de empurrar o corpo: um PDF de
 *    25 MB num link de 5 Mbps leva ~40s so de subida e estourava os 30s de
 *    forma deterministica, toda vez. Foi a regressao que a Onda 12 introduziu
 *    ao trocar `timeoutMs: 0` pelo default: a fatia protegeu os downloads e
 *    esqueceu que upload tambem e transferencia.
 *      - `knowledge.service.ts` → `uploadDocument`, `uploadMultipleDocuments`
 *                                 (N arquivos num request), `uploadDocumentAdmin`
 *      - `storage.ts`           → `storageService.uploadFile`
 *      - `voice-cloning.service.ts` → `uploadVoiceSamples` (N amostras)
 *      - `users.ts`             → `usersService.uploadAvatar`
 *
 * 3) POST JSON que o backend resolve INLINE e pode passar de 30s (3). Aqui o
 *    criterio nao e "e lento", e "o servidor faz o trabalho dentro do request
 *    E repetir/abandonar nao causa dano":
 *      - `prospecting.ts` → `enrichCompany`. O pior caso do backend e ~90s
 *        (playwright-runner 70s + BrasilAPI 10s + fallback ReceitaWS 10s).
 *        Idempotente: cacheado por CNPJ, a escrita e um update simples.
 *      - `audience.ts`    → `audienceService.preview`. Varredura ate 10k
 *        contatos + um `findMany` de opt-out sem filtro de id. Read-only,
 *        nao persiste nada — nao ha o que abandonar.
 *      - `sequences.ts`   → `enrollAudience`. Mesma varredura do preview antes
 *        do `createMany`. Idempotente pelo unique
 *        `[sequenceId, subjectType, subjectId]` + `skipDuplicates`, e abortar
 *        no meio e justamente o dano a evitar: as inscricoes ja gravaram e o
 *        usuario ve "falhou".
 *
 * O QUE NAO ENTRA, e por que (o default de 30s e a resposta certa para eles):
 *   - `proposals/:id/generate`, `leads/:id/enrich`, `workflows/:id/execute`:
 *     o backend so publica no RabbitMQ e devolve 202 em milissegundos. Estourar
 *     30s ali nao e "gerou devagar", e "o broker caiu" — e a falha e o que
 *     queremos ver.
 *   - `{leads,customers}/:id/merge`: transacao limitada pelo numero de
 *     duplicatas (tipicamente <1s) e com efeito destrutivo (soft-delete). 30s ja
 *     e 30x o esperado; passar disso significa transacao travada, e ai o abort
 *     e o comportamento correto.
 *   - `sequences/:id/enroll` (ids explicitos): so validacao + um `createMany`.
 *   - familia de endpoints `sync` (providers/sync-models, voice-config,
 *     google-calendar, meta-templates, sync-vapi): chamadas a API externa na casa
 *     de segundos. Duas delas (VAPI) nao tem timeout no proprio backend — os
 *     30s do cliente sao a UNICA barreira contra spinner eterno; afroxa-los
 *     piora, nao melhora.
 *   - `email/send/bulk`: nao-idempotente por definicao (cada chamada = mais
 *     envios reais). Nao ganha folga sem idempotency key no servidor.
 */
export const LONG_REQUEST_TIMEOUT_MS = 120_000;

function getExtraHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  // Add X-Tenant-Id header for multi-tenant isolation
  // Falls back to VITE_TENANT_ID for pre-login public API calls (plans, payment settings)
  const tenantId = useTenantStore.getState().tenant?.id || import.meta.env.VITE_TENANT_ID;
  if (tenantId) headers['X-Tenant-Id'] = tenantId;
  const companyId = useCompanyStore.getState().activeCompany?.id;
  if (companyId) headers['X-Company-Id'] = companyId;
  return headers;
}

export const api = createApiClient({
  baseUrl: API_URL,
  getToken: () => getToken(),
  getExtraHeaders,
  timeoutMs: DEFAULT_REQUEST_TIMEOUT_MS,
  maxRetries: 0,
});

// Requisicoes crm.* -> api.* sao CROSS-ORIGIN (subdominios diferentes, mesmo
// que same-site) — o navegador so envia/persiste o cookie httpOnly do
// api-core quando a request opta por incluir credentials. Com a flag OFF
// (default) isso nunca e setado: o Set-Cookie aditivo do backend chega mas o
// navegador o descarta silenciosamente, entao nenhum comportamento muda.
if (HTTPONLY_AUTH || SECURE_SESSION) api.defaults.withCredentials = true;
if (SECURE_SESSION) api.defaults.headers.common['X-Crm-Session'] = 'cookie';
let memoryAccessToken: string | null = null;
let memoryRefreshToken: string | null = null;
if (!isTutorialMode()) {
  if (!SECURE_SESSION) {
    // A legacy build cannot recover a browser session securely after reload.
    // Drop persisted auth state rather than reuse a refresh token exposed to JS.
    clearAuthPersistence();
  } else {
    for (const storage of [window.localStorage, window.sessionStorage]) {
      storage.removeItem(AUTH_TOKEN_KEY);
      storage.removeItem(AUTH_REFRESH_TOKEN_KEY);
    }
  }
}

// Modo demonstracao (spec `tutorial-crm` 6.1-2): o iframe do tutorial e' da
// MESMA origem da janela real e enxergaria (e apagaria) os tokens dela. Dentro
// do tutorial nao ha token nenhum: as requisicoes nunca saem do adaptador de
// demonstracao, e nenhuma das quatro funcoes abaixo toca as chaves reais.
export const getToken = (): string | null => {
  if (isTutorialMode()) return null;
  if (SECURE_SESSION) return memoryAccessToken;
  if (HTTPONLY_AUTH) return null;
  return memoryAccessToken;
};

/** Returns the short-lived JWT for the Socket.IO handshake only. */
export const getSocketToken = (): string | null => {
  if (isTutorialMode()) return null;
  return memoryAccessToken;
};

export const getRefreshToken = (): string | null => {
  if (isTutorialMode() || SECURE_SESSION) return null;
  return memoryRefreshToken;
};

export const setTokens = (accessToken: string, refreshToken?: string): void => {
  if (isTutorialMode()) return;
  memoryAccessToken = accessToken;
  memoryRefreshToken = SECURE_SESSION ? null : refreshToken ?? null;
};

export const clearTokens = (): void => {
  if (isTutorialMode()) return;
  // Os dois armazenamentos: a sessao pode estar em qualquer um deles.
  memoryAccessToken = null;
  memoryRefreshToken = null;
  clearAuthPersistence();
};

// Auth event emitter for clean logout redirect (avoids full page reload)
type AuthEventListener = () => void;
const authListeners: AuthEventListener[] = [];

export const onAuthLogout = (listener: AuthEventListener): (() => void) => {
  authListeners.push(listener);
  return () => {
    const idx = authListeners.indexOf(listener);
    if (idx >= 0) authListeners.splice(idx, 1);
  };
};

const emitAuthLogout = () => {
  if (authListeners.length > 0) {
    authListeners.forEach((fn) => fn());
  } else {
    // Fallback if no listener registered
    window.location.href = '/auth';
  }
};

// Request interceptor: Authorization + X-Tenant-Id/X-Company-Id agora vêm de
// `getToken`/`getExtraHeaders` na config do `createApiClient` acima (Onda 10).

// Response interceptor - Handle token refresh

/**
 * O interceptor de resposta de `createApiClient` (pacote `@oprus/api-client`)
 * SEMPRE converte a rejeição num `ApiError` (nunca preserva o `AxiosError`
 * original) — ver o comentário acima de `export const api`. Esta função
 * desfaz esse wrapping, devolvendo o erro ORIGINAL (`ApiError.raw`, que o
 * pacote sempre preenche em `fromAxiosError`) para que o restante deste
 * interceptor — e os ~80 services/hooks que chamam `isApiError`/
 * `axios.isAxiosError`/`getErrorMessage` sobre o erro rejeitado — continuem
 * operando sobre um `AxiosError` de verdade, exatamente como antes da Onda 10.
 */
export function unwrapClientError(error: unknown): unknown {
  if (isPackageApiError(error) && error.raw !== undefined) {
    return error.raw;
  }
  return error;
}

let isRefreshing = false;
/** Only the operation that acquired the refresh mutex releases it. */
function finishRefresh(): void { isRefreshing = false; }
function shouldExpireAfterRefresh(error: unknown): boolean {
  const failure = unwrapClientError(error) as AxiosError;
  return !SECURE_SESSION || failure?.response?.status === 401 || failure?.response?.status === 403;
}
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Renova o access token ANTES de a requisicao sair, quando ele esta' por
 * vencer.
 *
 * ## Incidente 2026-09-21 (item 7 / C11)
 *
 * `/notifications/unread-count` e `/tokens/balance` davam **401 intermitente**
 * logo apos navegar, com sessao valida. O access token vale 15 minutos e so'
 * era renovado DEPOIS de um request falhar — e quando varias consultas saem
 * juntas, TODAS levam 401 antes de a primeira renovar. O interceptor se
 * recupera (renova e repete), entao a tela funcionava; o que sobrava era um
 * rastro de 401 no console e uma rodada extra de ida e volta por lote.
 *
 * Reusa o MESMO mutex e a MESMA fila do caminho reativo: se ja' ha' um refresh
 * em voo, esta requisicao espera nele em vez de abrir um segundo.
 */
async function requestTokenRefresh() {
  if (SECURE_SESSION) {
    return withCookieSessionLock(async () => {
      const response = await axios.post<{ accessToken: string; refreshToken?: string }>(`${API_URL}/auth/refresh`, {}, {
      withCredentials: true, timeout: DEFAULT_REQUEST_TIMEOUT_MS,
      headers: { ...getExtraHeaders(), 'X-Crm-Session': 'cookie' },
    });
      if (!response.data.accessToken) throw new Error('Session refresh returned no access token');
      setTokens(response.data.accessToken);
      return response;
    });
  }
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error('Missing refresh token');
  const pair = await coordinateRefresh({
    scope: API_URL, refreshToken,
    read: () => {
      const accessToken = getToken(); const refreshToken = getRefreshToken();
      return refreshToken ? { accessToken: accessToken ?? '', refreshToken } : null;
    },
    request: async (value) => {
      const response = HTTPONLY_AUTH
        ? await axios.post(`${API_URL}/auth/refresh`, { refreshToken: value }, { withCredentials: true })
        : await axios.post(`${API_URL}/auth/refresh`, { refreshToken: value });
      return response.data;
    },
    persist: (pair) => setTokens(pair.accessToken, pair.refreshToken),
  });
  return { data: pair };
}

/** The one refresh mutex used by socket recovery and initial page restoration. */
export async function refreshAccessToken(): Promise<string> {
  if (isTutorialMode()) throw new Error('Live session refresh is unavailable in tutorial mode');
  if (isRefreshing) {
    return new Promise<string>((resolve, reject) => {
      failedQueue.push({ resolve: (token) => token ? resolve(token) : reject(new Error('Missing access token')), reject });
    });
  }
  isRefreshing = true;
  try {
    const response = await requestTokenRefresh();
    const { accessToken, refreshToken } = response.data;
    if (!accessToken) throw new Error('Session refresh returned no access token');
    if (!SECURE_SESSION) setTokens(accessToken, refreshToken);
    processQueue(null, accessToken);
    return accessToken;
  } catch (error) {
    processQueue(error instanceof Error ? error : new Error('Session refresh failed', { cause: error }), null);
    throw error;
  } finally { finishRefresh(); }
}

async function ensureFreshToken(): Promise<void> {
  // Flag ON: o access token vive no cookie httpOnly e o JS nao o ve — nao ha'
  // `exp` para ler, e o caminho reativo continua sendo o unico possivel.
  if (HTTPONLY_AUTH && !SECURE_SESSION) return;
  if (!shouldRefreshBeforeRequest(getToken())) return;
  if (!SECURE_SESSION && !getRefreshToken()) return;

  if (isRefreshing) {
    // Espera o refresh que ja' esta' em voo. Uma falha dele nao pode derrubar
    // esta requisicao aqui: o 401 (se vier) segue pelo caminho reativo, que e'
    // quem sabe deslogar.
    await new Promise<void>((resolve) => {
      failedQueue.push({ resolve: () => resolve(), reject: () => resolve() });
    });
    return;
  }

  isRefreshing = true;
  try {
    const refreshToken = getRefreshToken();
    if (!SECURE_SESSION && !refreshToken) return;

    const response = await requestTokenRefresh();
    const { accessToken, refreshToken: newRefreshToken } = response.data;
    if (!SECURE_SESSION) setTokens(accessToken, newRefreshToken);
    processQueue(null, accessToken);
  } catch (error) {
    // oprus-allow:swallow — renovacao preventiva e' otimizacao. Falhou? A
    // requisicao sai com o token velho e o 401 aciona o caminho reativo, que
    // e' quem decide entre renovar de novo e deslogar.
    processQueue(null, getToken());
    void reportErrorDeferred(new Error(getFriendlyError(error)), { kind: 'session.preventive-refresh' });
  } finally {
    finishRefresh();
  }
}

api.interceptors.request.use(async (config) => {
  const url = config.url || '';
  // `/auth/*` nunca depende do access token — e renovar aqui daria laço.
  if (PUBLIC_AUTH_ENDPOINTS.some((path) => url.includes(path))) return config;

  await ensureFreshToken();

  // O token pode ter mudado agora mesmo; o header e' remontado com o atual.
  const token = getToken();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

type RetryableRequest = InternalAxiosRequestConfig & { _retry?: boolean };
function canRetryUnauthorized(error: AxiosError, request: RetryableRequest | undefined): request is RetryableRequest {
  return error.response?.status === 401 && Boolean(request) && !request?._retry;
}

function retryAuthenticatedRequest(request: InternalAxiosRequestConfig, token: string) {
  if ((!HTTPONLY_AUTH || SECURE_SESSION) && request.headers) {
    request.headers.Authorization = `Bearer ${token}`;
  }
  return api(request);
}

api.interceptors.response.use(
  (response) => {
    // Unwrap the { data: ... } wrapper from API responses
    // But keep paginated responses intact (they have both 'data' and 'meta')
    if (
      response.data &&
      typeof response.data === 'object' &&
      'data' in response.data &&
      !('meta' in response.data) // Don't unwrap paginated responses
    ) {
      response.data = response.data.data;
    }
    return response;
  },
  async (rawError: unknown) => {
    // Ver `unwrapClientError` acima: recupera o `AxiosError`/`Error` ORIGINAL
    // de dentro do `ApiError` que `createApiClient` sempre produz.
    const error = unwrapClientError(rawError) as AxiosError;

    // Normalize the error message centrally so callers that just read
    // `error.message` (instead of calling getFriendlyError themselves) stop
    // surfacing raw axios noise like "Request failed with status code 500".
    // `error.response.data.message` (read by getErrorMessage/getFriendlyError)
    // is untouched, so callers that already normalize explicitly are unaffected.
    if (axios.isAxiosError(error)) {
      error.message = getFriendlyError(error);
    }

    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const requestUrl = originalRequest?.url || '';
    const isPublicAuthRequest = PUBLIC_AUTH_ENDPOINTS.some((path) => requestUrl.includes(path));

    if (isPublicAuthRequest) {
      return Promise.reject(error);
    }

    // If error is 401 and we haven't tried to refresh yet
    if (canRetryUnauthorized(error, originalRequest)) {
      originalRequest._retry = true;
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => retryAuthenticatedRequest(originalRequest, token));
      }

      isRefreshing = true;

      // Onda 7 / 7.1.6 fix: EVERY exit path from here on must reset
      // `isRefreshing`, including the early "no refresh token" return below.
      // Before this fix, that early `return` skipped the `finally` (it lived
      // outside the try/catch), leaving the module-level mutex stuck at
      // `true` forever — any later 401 (even from a fresh, valid session)
      // would see `isRefreshing === true` and queue behind a refresh that
      // was never actually in flight, hanging indefinitely instead of
      // resolving or rejecting.
      try {
        const refreshToken = getRefreshToken();
        if (!SECURE_SESSION && !refreshToken) {
          // Reject anything that may have queued (defensive — see comment
          // above; in practice nothing can queue between setting
          // `isRefreshing = true` and this synchronous check).
          processQueue(error, null);
          clearTokens();
          emitAuthLogout();
          return Promise.reject(error);
        }

        // Flag ON: withCredentials so the rotated access-token cookie the
        // backend sets on this response (Set-Cookie, additive) is actually
        // stored by the browser — this is a cross-origin request from the
        // CRM's perspective (see the HTTPONLY_AUTH doc comment above).
        // NOTE: branched (not a 3rd `undefined` arg) so the flag-OFF call
        // keeps its exact pre-Onda-11 arity/shape.
        const response = await requestTokenRefresh();

        const { accessToken, refreshToken: newRefreshToken } = response.data;
        if (!SECURE_SESSION) setTokens(accessToken, newRefreshToken);

        processQueue(null, accessToken);

        // Flag ON: the fresh cookie set above already authenticates the
        // retried request via withCredentials — never set Authorization for
        // the access token.
        return retryAuthenticatedRequest(originalRequest, accessToken);
      } catch (refreshError) {
        processQueue(refreshError as Error, null);
        if (shouldExpireAfterRefresh(refreshError)) {
          clearTokens();
          emitAuthLogout();
        }
        return Promise.reject(refreshError);
      } finally {
        finishRefresh();
      }
    }

    return Promise.reject(error);
  }
);

// Registrado DEPOIS do interceptor acima: recebe o AxiosError já desembrulhado.
installCompanyContextRecovery(api);

// API Error type
export interface ApiError {
  message: string;
  statusCode: number;
  error?: string;
  /** Machine-readable error code (e.g. INVITE_CODE_NOT_FOUND). */
  code?: string;
  errors?: Array<{ field?: string; message: string }>;
}

export const isApiError = (error: unknown): error is AxiosError<ApiError> => {
  return axios.isAxiosError(error);
};

/**
 * P14 (F9-01) — o fallback desta funcao era o literal pt-BR
 * `'Erro desconhecido'`, em duas linhas. Ele chega a toast, banner e
 * `<SectionError>` de todo o app, entao vazava portugues para en-US/es-ES.
 *
 * `getErrorMessage` NAO e um componente React, entao nao ha `useTranslation`
 * aqui — traduz-se pela instancia do i18next, o mesmo padrao ja usado em
 * `stores/chat-store.slices/socket.slice.ts`, `components/ErrorBoundary.tsx`
 * e `hooks/useProposals.ts`.
 */
const unknownErrorMessage = (): string => i18n.t('errors:api.unknown');

export const getErrorMessage = (error: unknown): string => {
  if (isApiError(error)) {
    const data = error.response?.data;
    if (data?.code === 'FEATURE_NOT_IN_PLAN' || data?.code === 'FEATURE_CACHE_INVALIDATION_FAILED') {
      return i18n.t(`errors:codes.${data.code}`);
    }
    if (data?.errors && data.errors.length > 0) {
      return data.errors.map((e) => e.message).join('. ');
    }
    return data?.message || error.message || unknownErrorMessage();
  }
  if (error instanceof Error) {
    return error.message;
  }
  return unknownErrorMessage();
};

/** Extract the backend `code` field from an axios error response, if present. */
export const getErrorCode = (error: unknown): string | undefined => {
  if (isApiError(error)) {
    return error.response?.data?.code;
  }
  return undefined;
};

/** Extract the HTTP status from an axios error, if present. */
export const getErrorStatus = (error: unknown): number | undefined => {
  if (isApiError(error)) {
    return error.response?.status;
  }
  return undefined;
};

// ============================================================
// Envelope unwrap helpers (Fatia 1.2 — fundacao da camada de dados)
// ============================================================
//
// The response interceptor above unwraps a bare `{ data: <payload> }`
// envelope automatically, but skips it whenever the payload also has a
// `meta` key (to keep `{ data, meta }` paginated envelopes intact) — and
// does nothing for other envelope shapes (e.g. `{ data, hasMore }`). Several
// services ended up reimplementing the same unwrap-and-normalize logic by
// hand; use these two helpers instead of writing another copy.

/**
 * Normalizes a raw `{ data: T[], meta: {...} }` list envelope into the
 * frontend's `{ items: T[], meta: {...} }` shape, coercing `page`/`limit`
 * to numbers (the backend may serialize them as strings) and optionally
 * mapping each item (e.g. to translate a raw API shape into a domain type).
 */
export function unwrapPaginated<T, R = T>(
  raw: ApiPaginatedResponse<T>,
  mapItem?: (item: T) => R
): PaginatedResponse<R> {
  const items = raw?.data ?? [];
  return {
    items: mapItem ? items.map(mapItem) : (items as unknown as R[]),
    meta: {
      total: raw?.meta?.total ?? 0,
      page: Number(raw?.meta?.page ?? 1),
      limit: Number(raw?.meta?.limit ?? items.length),
      totalPages: raw?.meta?.totalPages ?? 1,
    },
  };
}

/**
 * Defensively unwraps a single-resource `{ data: T }` envelope.
 *
 * The response interceptor already does this for the common case, but it
 * bails out whenever the envelope also has a `meta`/other sibling key, or
 * when `data` is an array sitting next to unrelated keys (e.g. the payment
 * history `{ data: T[], hasMore }` shape). Use this at the service call site
 * when the interceptor's blanket heuristic cannot be trusted for a given
 * endpoint, instead of re-implementing the same `'data' in raw` check.
 */
export function unwrapEnvelope<T>(raw: unknown): T | null {
  if (raw && typeof raw === 'object' && !Array.isArray(raw) && 'data' in (raw as Record<string, unknown>)) {
    return ((raw as { data: T | null }).data) ?? null;
  }
  return (raw as T) ?? null;
}
